import { Outlet } from 'react-router-dom';
import {
  FaArrowLeft, FaTimes, FaEnvelope, FaEdit, FaCopy, FaCamera, FaUser, FaBuilding, FaGlobe, FaExternalLinkAlt, FaPhone, FaLink,
  FaClipboardList, FaFacebook, FaInstagram, FaLinkedin, FaTwitter
} from 'react-icons/fa';
import TextEdit from '../../../molecules/TextEdit';
import NewTooltip from '../../../atoms/NewTooltip';
import CallRegistryModalForm from './CallRegistryModalForm';
import LabelCopy from './LabelCopy';
import Tab, { TabProps } from '../../../molecules/Tab';
import ContactAboutTab from './ContactAboutTab';
import ContactActivityTab from './ContactActivityTab';
import MiniLoading from '../../../ions/MiniLoading';
import useContactPageView, { TAG_COLORS, formatLongDate, copyToClipboard } from './useContactPageView';

export default function ContactPageView() {
  const {
    navigate, id, contact, isLoading, error, fileInputRef,
    activeTab, setActiveTab, tabItems, activeTabId,
    newTag, setNewTag, addingTag, customColor, setCustomColor, editingTagId,
    handleRemoveTag, handleTagClick, handleColorChange, handleKeyPress,
    showCallModal, setShowCallModal,
    interactionToRemove, setInteractionToRemove, removingInteraction,
    openMenuId, setOpenMenuId, setLoadingContactPoint,
    confirmDeleteId, setConfirmDeleteId, confirmDeleteLabel, setConfirmDeleteLabel,
    deletingContactPoint,
    handleImageUpload, handleDeleteContactPoint,
    handleRegisterCall, handleRemoveInteraction,
    onContactUpdate, onContactPointUpdate,
  } = useContactPageView();

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-slate-900">
        <div className="flex-1 overflow-auto">
          <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded animate-pulse" />
              <div className="flex-1">
                <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-48 mb-2 animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-32 animate-pulse" />
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 space-y-4">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full animate-pulse" />
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 animate-pulse" />
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-slate-900 items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Erro ao carregar contato</p>
          <button onClick={() => navigate('/communication/contact')} className="mt-4 text-blue-600 dark:text-blue-400">Voltar para listagem</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-slate-900">
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <button onClick={() => navigate('/communication/contact/0/10/name/ASC/', { state: { refresh: true } })} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4">
              <FaArrowLeft /> Voltar para lista
            </button>
          </div>

          {/* Contact Info with Image */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600">
                  {contact.image ? (
                    <img src={contact.image} alt={contact.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                      {contact.type === 'PF' ? <FaUser size={48} /> : <FaBuilding size={48} />}
                    </div>
                  )}
                  <button disabled onClick={() => fileInputRef.current?.click()} className="absolute bottom-2 right-2 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    <FaCamera size={14} />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">{contact.name}</h1>
                <div className="flex items-center gap-2 mb-1">
                  <LabelCopy text={contact.document} tooltip="Copiar CNPJ" />
                  {contact.type === 'PJ' && contact.rootDocument && (
                    <>
                      <span className="text-gray-400">|</span>
                      <LabelCopy text={contact.rootDocument} tooltip="Copiar CNPJ raiz" />
                    </>
                  )}
                </div>
                {contact.website && /^https?:\/\//i.test(contact.website) && (
                  <div className="flex items-center gap-2 mb-1">
                    <FaGlobe className="text-blue-500" />
                    <a href={contact.website} target="_blank" rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline text-sm">{contact.website}</a>
                    <NewTooltip content="Abrir em nova aba" alignment="top">
                      <a href={contact.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                        <FaExternalLinkAlt size={12} />
                      </a>
                    </NewTooltip>
                    <NewTooltip content="Copiar link" alignment="top">
                      <button onClick={() => copyToClipboard(contact.website!)} className="text-blue-500 hover:text-blue-700">
                        <FaCopy size={12} />
                      </button>
                    </NewTooltip>
                  </div>
                )}
                {contact.socialMedia && (
                  <div className="flex items-center gap-2 mb-1">
                    {contact.socialMedia.facebook && (
                      <NewTooltip content="Abrir Facebook" alignment="top">
                        <a href={contact.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"><FaFacebook size={18} /></a>
                      </NewTooltip>
                    )}
                    {contact.socialMedia.instagram && (
                      <NewTooltip content="Abrir Instagram" alignment="top">
                        <a href={contact.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300"><FaInstagram size={18} /></a>
                      </NewTooltip>
                    )}
                    {contact.socialMedia.linkedin && (
                      <NewTooltip content="Abrir LinkedIn" alignment="top">
                        <a href={contact.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"><FaLinkedin size={18} /></a>
                      </NewTooltip>
                    )}
                    {contact.socialMedia.twitter && (
                      <NewTooltip content="Abrir Twitter" alignment="top">
                        <a href={contact.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-600 dark:text-sky-400 dark:hover:text-sky-300"><FaTwitter size={18} /></a>
                      </NewTooltip>
                    )}
                  </div>
                )}
                {contact.city && contact.state && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">{contact.city} - {contact.state}</p>
                )}
              </div>
              <div className="flex-shrink-0 flex gap-3">
                <NewTooltip content="Registrar ligação" alignment="top">
                  <button onClick={() => setShowCallModal(true)} className="p-4 bg-white dark:bg-slate-700 border-2 border-green-600 dark:border-green-500 text-green-600 dark:text-green-500 rounded-full hover:bg-green-50 dark:hover:bg-slate-600"><FaPhone size={20} /></button>
                </NewTooltip>
                <NewTooltip content="Enviar email" alignment="top">
                  <button disabled className="p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"><FaEnvelope size={16} /></button>
                </NewTooltip>
                <NewTooltip content="Registrar atividade" alignment="top">
                  <button disabled className="p-3 bg-gray-100 dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"><FaClipboardList size={16} /></button>
                </NewTooltip>
                <NewTooltip content={`Vincular ${contact.type === 'PF' ? 'Empresa' : 'Pessoa'}`} alignment="top">
                  <button disabled className="p-3 bg-gray-100 dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"><FaLink size={16} /></button>
                </NewTooltip>
                <NewTooltip content="Editar contato" alignment="top">
                  <button onClick={() => navigate(`/communication/contact/${id}/page-view/edit`)} className="p-3 bg-gray-100 dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"><FaEdit size={16} /></button>
                </NewTooltip>
              </div>
            </div>
            {contact.createdAt && (
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">Criado em: {formatLongDate(contact.createdAt)}</div>
            )}
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <Tab tabItems={tabItems as TabProps[]} tabIndex={activeTab} setTabIndex={setActiveTab} />
          </div>

          {/* About Tab */}
          {activeTabId === 'about' && (
            <ContactAboutTab
              contact={contact}
              contactId={id!}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              setLoadingContactPoint={setLoadingContactPoint}
              setConfirmDeleteId={setConfirmDeleteId}
              setConfirmDeleteLabel={setConfirmDeleteLabel}
            />
          )}

          {activeTabId === 'activity' && contact.interactions && (
            <ContactActivityTab contact={contact} onRemove={setInteractionToRemove} />
          )}

          {/* Overview Tab */}
          {activeTabId === 'overview' && (
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Visão Geral</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Conteúdo da visão geral em desenvolvimento.</p>
            </div>
          )}

          {/* Companies Tab */}
          {activeTabId === 'companies' && contact.companies && (
            <div className="grid grid-cols-3 gap-3">
              {contact.companies.map((company) => (
                <button key={company.id} onClick={() => navigate(`/communication/contact/${company.id}/page-view`)}
                  className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{company.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">CNPJ: {company.document}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"><FaBuilding size={10} />{company.role}</span>
                </button>
              ))}
            </div>
          )}

          {/* People Tab */}
          {activeTabId === 'people' && contact.people && (
            <div className="grid grid-cols-3 gap-3">
              {contact.people.map((person) => (
                <button key={person.id} onClick={() => navigate(`/communication/contact/${person.id}/page-view`)}
                  className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{person.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">CPF: {person.document}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"><FaUser size={10} />{person.role}</span>
                </button>
              ))}
            </div>
          )}

          {/* Branches Tab */}
          {activeTabId === 'branches' && contact.branches && (
            <div className="grid grid-cols-3 gap-3">
              {contact.branches.map((branch) => {
                const roleColors: Record<string, string> = {
                  MATRIZ: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
                  COLIGADA: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
                };
                const roleLabels: Record<string, string> = { MATRIZ: 'Matriz', COLIGADA: 'Coligada' };
                const colorClass = roleColors[branch.role ?? ''] ?? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
                const roleLabel = roleLabels[branch.role ?? ''] ?? 'Filial';
                return (
                <button key={branch.id} onClick={() => navigate(`/communication/contact/${branch.id}/page-view`)}
                  className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left">
                  <div className="mb-2">
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm">{branch.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{branch.document}</p>
                    {(branch.city || branch.state) && <p className="text-xs text-gray-500 dark:text-gray-400">{branch.city}{branch.city && branch.state ? ' - ' : ''}{branch.state}</p>}
                  </div>
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${colorClass}`}><FaBuilding size={10} />{roleLabel}</span>
                </button>
                );
              })}
            </div>
          )}

          {/* Deals Tab */}
          {activeTabId === 'deals' && contact.deals && (
            <div className="grid grid-cols-3 gap-3">
              {contact.deals.map((deal) => (
                <div key={deal.id} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 text-xs">
                      <button onClick={() => navigate(`/operations/${deal.operation}`)} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">{deal.operation}</button>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-700 dark:text-gray-300 font-medium">Nº {deal.number}</span>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${deal.status === 'Validada' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'}`}>{deal.status}</span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-white truncate mb-1" title={deal.assignor}>{deal.assignor}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{deal.valueFormatted ?? deal.value}</span>
                    <span>Venc. {new Date(deal.dueDate).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* History and Tags Sidebar */}
      <div className="w-80 bg-white dark:bg-slate-800 border-l border-gray-200 dark:border-slate-700 overflow-auto">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Histórico</h2>
          <div className="space-y-4 mb-6">
            {contact.history.slice(0, 10).map((item) => (
              <div key={item.id} className="border-l-2 border-blue-500 pl-4">
                <NewTooltip content={item.action} alignment="top">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: '1.25rem', maxHeight: '2.5rem' }}>{item.action}</p>
                </NewTooltip>
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.user}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{item.timestamp}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Tags</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {contact.tags.map((tag) => (
              <div key={tag.id} className="relative">
                <span
                  role={tag.canEditColor ? "button" : undefined}
                  tabIndex={tag.canEditColor ? 0 : undefined}
                  onClick={() => tag.canEditColor ? handleTagClick(tag.id) : null}
                  onKeyDown={(e) => { if (tag.canEditColor && (e.key === 'Enter' || e.key === ' ')) handleTagClick(tag.id); }}
                  style={{ backgroundColor: tag.color }}
                  className={`${tag.id === editingTagId ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-white' : ''} text-white px-3 py-1 rounded-full text-sm flex items-center gap-2 ${tag.canEditColor ? 'cursor-pointer hover:opacity-80' : ''}`}
                >
                  {tag.label}
                  {tag.canRemove && (
                    <button onClick={(e) => { e.stopPropagation(); handleRemoveTag(tag.label); }} className="hover:text-gray-200"><FaTimes size={12} /></button>
                  )}
                </span>
                {editingTagId === tag.id && (
                  <div className="absolute top-full mt-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg p-3 z-10">
                    <div className="flex flex-wrap gap-2 w-48">
                      {TAG_COLORS.map((color) => (
                        <button key={color.value} onClick={() => handleColorChange(color.value)} style={{ backgroundColor: color.value }} className="w-7 h-7 rounded-full hover:scale-110 transition-transform border-2 border-transparent hover:border-white" title={color.label} />
                      ))}
                      <div className="w-full mt-2 flex items-center gap-2">
                        <input type="color" value={customColor} onChange={(e) => setCustomColor(e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0 p-0" />
                        <button onClick={() => handleColorChange(customColor)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Aplicar</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <TextEdit label="" value={newTag} onChange={(e) => setNewTag(e.target.value.toUpperCase().replace(/\s/g, ''))} onKeyPress={handleKeyPress} placeholder="Nova tag..." disabled={addingTag} />
            </div>
            {addingTag && <MiniLoading />}
          </div>
        </div>
      </div>

      {/* Modals */}
      {interactionToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Remover atividade</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Tem certeza que deseja remover esta atividade? Esta ação não pode ser desfeita.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setInteractionToRemove(null)} disabled={removingInteraction} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded hover:bg-gray-200 dark:hover:bg-slate-600">Cancelar</button>
              <button onClick={handleRemoveInteraction} disabled={removingInteraction} className="px-4 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50">{removingInteraction ? 'Removendo...' : 'Remover'}</button>
            </div>
          </div>
        </div>
      )}

      {showCallModal && <CallRegistryModalForm contactId={id!} onClose={() => setShowCallModal(false)} onSuccess={handleRegisterCall} />}

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Confirmar remoção</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Deseja remover <strong>{confirmDeleteLabel}</strong>? Esta ação não pode ser desfeita.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDeleteId(null)} disabled={deletingContactPoint} className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 disabled:opacity-50">Cancelar</button>
              <button onClick={() => handleDeleteContactPoint(confirmDeleteId)} disabled={deletingContactPoint} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">{deletingContactPoint ? 'Removendo...' : 'Remover'}</button>
            </div>
          </div>
        </div>
      )}

      <Outlet context={{ contact, onContactUpdate, onContactPointUpdate }} />
    </div>
  );
}
